/**
 * MicPermissionHint — web-only banner shown when the room mic acquisition was
 * denied (AgoraRTCException PERMISSION_DENIED). On web the site mic permission
 * is sticky and the browser won't re-prompt, so the user is stuck muted until
 * they re-grant it by hand — and WHERE to do that differs per browser.
 *
 * We detect the browser from the userAgent and show the matching mini-guide
 * (iOS Safari vs Chrome-on-iOS vs Android Chrome vs desktop Chrome/Safari/
 * Firefox), instead of a generic «разрешите микрофон» the user can't action.
 *
 * Native builds handle mic permission through the OS prompt, so this renders
 * nothing off web.
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, ViewStyle, TextStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

type BrowserGuide = { label: string; steps: string };

/**
 * CORE-1197 (11.09.2026) — тот же рецепт нужен и камере: гость, не давший
 * доступ перед входом, включает её из комнаты, и отказ обязан объясняться так
 * же подробно, как отказ микрофона. Меняется одно слово, поэтому это параметр,
 * а не вторая копия инструкций: разъехавшись, они начнут вести человека не в ту
 * строку настроек браузера.
 */
export type PermissionDevice = 'mic' | 'cam';
const DEVICE_WORD: Record<PermissionDevice, { nominative: string; accusative: string; press: string }> = {
  mic: { nominative: 'Микрофон', accusative: 'микрофону', press: 'микрофон' },
  cam: { nominative: 'Камера', accusative: 'камере', press: 'камеру' },
};

/** Map the current browser to a concrete «where to re-allow the device» recipe.
 *
 * CORE-1954 — no «обновите страницу» where the browser applies the new site
 * permission live (Chrome, Edge, Chrome на Android: dev 28.10.2026 16:04:12 —
 * `permission_state change → granted` без перезагрузки, следующее нажатие
 * работает). Safari and Firefox may keep a refusal until the page is reloaded,
 * so there reload is the fallback, not the first step. */
export function detectMicPermissionGuide(ua: string, device: PermissionDevice = 'mic'): BrowserGuide {
  const word = DEVICE_WORD[device].nominative;
  const pressAgain = `Затем снова нажмите на ${DEVICE_WORD[device].press} — обновлять страницу не нужно.`;
  const pressAgainOrReload = `Затем снова нажмите на ${DEVICE_WORD[device].press}. Если доступ не появится, обновите страницу.`;
  const isIOS = /iP(hone|ad|od)/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isCriOS = /CriOS/.test(ua);   // Chrome engine tag on iOS
  const isFxiOS = /FxiOS/.test(ua);   // Firefox on iOS
  const isEdge = /Edg/.test(ua);
  const isOpera = /OPR|Opera/.test(ua);
  const isChrome = /Chrome|CriOS/.test(ua) && !isEdge && !isOpera;
  const isFirefox = /Firefox|FxiOS/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|Android|Edg|OPR|FxiOS/.test(ua);

  // iOS: every browser is WebKit, but the permission toggle lives in a
  // different place per app.
  if (isIOS && isCriOS) return { label: 'Chrome на iPhone/iPad', steps: `Откройте «Настройки» телефона → Chrome → ${word} → включите. Затем вернитесь и перезайдите в Live-сессию.` };
  if (isIOS && isFxiOS) return { label: 'Firefox на iPhone/iPad', steps: `Откройте «Настройки» телефона → Firefox → ${word} → включите, затем перезайдите в Live-сессию.` };
  if (isIOS) return { label: 'Safari на iPhone/iPad', steps: `Нажмите «аА» слева в адресной строке → «Настройки для этого веб-сайта» → ${word} → «Разрешить». ${pressAgainOrReload}` };

  // Android
  if (isAndroid && isChrome) return { label: 'Chrome на Android', steps: `Нажмите на значок «🔒» слева от адреса → «Разрешения» → ${word} → «Разрешить». ${pressAgain}` };
  if (isAndroid && isFirefox) return { label: 'Firefox на Android', steps: `Нажмите «🔒» слева от адреса → «Изменить настройки» → разрешите ${word}. ${pressAgainOrReload}` };

  // Desktop
  if (isEdge) return { label: 'Microsoft Edge', steps: `Нажмите на «🔒» слева от адреса → Разрешения для сайта → ${word} → «Разрешить». ${pressAgain}` };
  if (isChrome) return { label: 'Google Chrome', steps: `Нажмите на значок «🔒»/настроек слева от адреса → ${word} → «Разрешить». ${pressAgain}` };
  if (isFirefox) return { label: 'Firefox', steps: `Нажмите на значок разрешений слева от адреса → ${word} → «Разрешить». ${pressAgainOrReload}` };
  if (isSafari) return { label: 'Safari (Mac)', steps: `Меню Safari → «Настройки для этого веб-сайта…» → ${word} → «Разрешить». ${pressAgainOrReload}` };

  return { label: 'ваш браузер', steps: `Разрешите доступ к ${DEVICE_WORD[device].accusative} для этого сайта в настройках браузера. ${pressAgainOrReload}` };
}

export const MicPermissionHint: React.FC<{ visible: boolean; onDismiss?: () => void }> = ({ visible, onDismiss }) => {
  if (!visible || Platform.OS !== 'web') return null;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const guide = detectMicPermissionGuide(ua);
  return (
    <View style={styles.wrap} accessibilityRole="alert">
      <Ionicons name="mic-off" size={18} color="#fff" style={styles.icon} />
      <View style={styles.body}>
        <Text style={styles.title}>Микрофон заблокирован · {guide.label}</Text>
        <Text style={styles.steps}>{guide.steps}</Text>
      </View>
      {onDismiss ? (
        <TouchableOpacity onPress={onDismiss} accessibilityLabel="Скрыть подсказку" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="close" size={18} color="rgba(255,255,255,0.85)" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

/**
 * CamPermissionHint — CORE-1197 (11.09.2026). То же самое для камеры.
 *
 * Повод тот же, что у микрофона: гость (или участник) не дал доступ перед
 * входом, включает камеру уже в комнате и получает отказ. До этой работы отказ
 * камеры не показывался вовсе — ни диалога, ни объяснения: кнопка просто
 * ничего не делала (замер 11.09.2026 — ноль запросов доступа на нажатие).
 */
export const CamPermissionHint: React.FC<{ visible: boolean; onDismiss?: () => void }> = ({ visible, onDismiss }) => {
  if (!visible || Platform.OS !== 'web') return null;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const guide = detectMicPermissionGuide(ua, 'cam');
  return (
    <View style={styles.wrap} accessibilityRole="alert">
      <Ionicons name="videocam-off" size={18} color="#fff" style={styles.icon} />
      <View style={styles.body}>
        <Text style={styles.title}>Камера заблокирована · {guide.label}</Text>
        <Text style={styles.steps}>{guide.steps}</Text>
      </View>
      {onDismiss ? (
        <TouchableOpacity onPress={onDismiss} accessibilityLabel="Скрыть подсказку" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="close" size={18} color="rgba(255,255,255,0.85)" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

/**
 * MicPublishFailedHint — CORE-1291. Shown when the mic acquired permission fine
 * but the audio could not be confirmed on the RTC wire (publish threw, or the
 * re-publish after a reconnect drop failed). The permission guide above does not
 * apply — nothing to re-allow — so this is a distinct, actionable message:
 * the room can't hear you, tap the mic again. Web-only, like its sibling.
 */
export const MicPublishFailedHint: React.FC<{ visible: boolean; onDismiss?: () => void }> = ({ visible, onDismiss }) => {
  if (!visible || Platform.OS !== 'web') return null;
  return (
    <View style={styles.wrap} accessibilityRole="alert">
      <Ionicons name="alert-circle" size={18} color="#fff" style={styles.icon} />
      <View style={styles.body}>
        <Text style={styles.title}>Вас пока не слышно</Text>
        <Text style={styles.steps}>Микрофон включён, но связь прервалась и звук не дошёл до участников. Нажмите на микрофон ещё раз, чтобы восстановить голос.</Text>
      </View>
      {onDismiss ? (
        <TouchableOpacity onPress={onDismiss} accessibilityLabel="Скрыть подсказку" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="close" size={18} color="rgba(255,255,255,0.85)" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

/**
 * MicSwitchFailedHint — CORE-1572. The picked microphone could not be opened
 * (unplugged, busy, iPhone out of range). The previous mic is still on air and
 * the picker already points back at it — the hint says so, so that a switch
 * that did not happen does not look like one that did (AC #2). Web-only.
 */
export const MicSwitchFailedHint: React.FC<{ visible: boolean; onDismiss?: () => void }> = ({ visible, onDismiss }) => {
  if (!visible || Platform.OS !== 'web') return null;
  return (
    <View style={styles.wrap} accessibilityRole="alert">
      <Ionicons name="alert-circle" size={18} color="#fff" style={styles.icon} />
      <View style={styles.body}>
        <Text style={styles.title}>Не удалось переключить микрофон</Text>
        <Text style={styles.steps}>Выбранное устройство недоступно, поэтому вас слышно через прежний микрофон. Проверьте, что оно подключено, и выберите его снова.</Text>
      </View>
      {onDismiss ? (
        <TouchableOpacity onPress={onDismiss} accessibilityLabel="Скрыть подсказку" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="close" size={18} color="rgba(255,255,255,0.85)" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

/**
 * MicDefaultFallbackHint — CORE-1803. The saved microphone gave no sound, so the
 * OS default was opened instead and the picker now names it. Without this the
 * person would not know their chosen device was swapped (QA 23.10). Web-only.
 */
export const MicDefaultFallbackHint: React.FC<{ label: string | null; onDismiss?: () => void }> = ({ label, onDismiss }) => {
  if (label === null || Platform.OS !== 'web') return null;
  const which = label ? `«${label}» — микрофон системы по умолчанию` : 'микрофон системы по умолчанию';
  return (
    <View style={styles.wrap} accessibilityRole="alert">
      <Ionicons name="swap-horizontal" size={18} color="#fff" style={styles.icon} />
      <View style={styles.body}>
        <Text style={styles.title}>Включён другой микрофон</Text>
        <Text style={styles.steps}>Выбранный микрофон не отвечает, поэтому вас слышно через {which}. Другой можно выбрать в настройках устройств.</Text>
      </View>
      {onDismiss ? (
        <TouchableOpacity onPress={onDismiss} accessibilityLabel="Скрыть подсказку" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="close" size={18} color="rgba(255,255,255,0.85)" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

/**
 * MicSilentHint — CORE-1572. The mic is on and published, but the sender has
 * carried dead silence for a while: the picked device gives no sound (an
 * iPhone that macOS lists but cannot hear, prod 01.10 / QA 02.10). Without
 * this the button stays lit and the person talks into nothing. Disappears by
 * itself once sound flows again or the mic is turned off. Web-only.
 */
export const MicSilentHint: React.FC<{ visible: boolean }> = ({ visible }) => {
  if (!visible || Platform.OS !== 'web') return null;
  return (
    <View style={styles.wrap} accessibilityRole="alert">
      <Ionicons name="mic-off" size={18} color="#fff" style={styles.icon} />
      <View style={styles.body}>
        <Text style={styles.title}>Микрофон не передаёт звук</Text>
        <Text style={styles.steps}>Сейчас вас не слышно: выбранное устройство молчит. Выберите другой микрофон в настройках или проверьте, что это устройство работает.</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: '#B54708', // amber-800 — attention, not alarming red
  } as ViewStyle,
  icon: { marginTop: 1 } as any,
  body: { flex: 1, gap: 2 } as ViewStyle,
  title: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  } as TextStyle,
  steps: {
    color: 'rgba(255,255,255,0.92)',
    fontSize: 12,
    lineHeight: 17,
  } as TextStyle,
});

export default MicPermissionHint;
